/* ⚠️ GENERATED from app/services/avatar3d.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/avatar3d.ts — one avatar, every surface: the rules both clients share.
//
// 🟥 THE FEATURE IS "A 3D AVATAR EVERYWHERE", AND "EVERYWHERE" IS THE TRAP.
//    A chat list is sixty rows; a GL context is a few megabytes of GPU memory
//    and a render loop each; sixty of them is the JS thread choking on a list
//    that used to scroll. So the avatar has TWO forms that must be
//    indistinguishable at rest:
//
//      • a PICTURE — a PNG we render ourselves from the same scene, at creation,
//        and store as the ordinary profile picture. Every list, comment, feed
//        row, widget, push notification and web pane draws it with the code it
//        already has, on both clients, with no new dependency. This is the
//        default form everywhere.
//      • a LIVE VIEW — the same scene, live, in the few places that MOVE:
//        the profile (full body, rollable), the chat header, the tapped-avatar
//        viewer, and the chat-list rows that are on screen AND allowed to move.
//        Capped by the budget below; a row that cannot get a slot draws the
//        picture, which looks the same.
//
//    Because the picture is rendered by the same framing code as the live view
//    (services/avatarScene.ts), swapping one for the other is invisible.
//
// 🟥 THE AVATAR IS NOT A SECOND PROFILE PICTURE. It IS the profile picture while
//    it exists: creating one sets `profileImage` (and the auth photoURL) to the
//    rendered PNG and keeps the photo it replaced in `avatarBackup`; removing it
//    restores that photo. That single decision is what makes "render everywhere"
//    true without touching the seventy call sites that read `profileImage`.
//
// 🟥 NO VENDOR. Ready Player Me was bought and switched off (Jan 2026); the two
//    selfie-to-avatar services left charge $800 a month. The avatar is OURS:
//    a spec (services/avatarKit.ts) assembled from a catalogue of parts we
//    generate and serve ourselves. The profile stores the spec; every viewer
//    builds the figure from parts it caches once. There is nothing to go dark.
//
// 🟢 WHAT MAY MOVE IS A PRIVACY QUESTION, and the answer is the one the app
//    already gives: `canSeeOnline` from utils/presenceVisibility — the viewer's
//    own sharing, the target's privacy settings, contacts-only, and the per-chat
//    override from ChatInfoScreen. If I may not see that you are online, your
//    avatar must not tell me by waving. `avatarMayMove` takes that flag; it
//    does not re-derive it.
//
// 🟢 PURE. No React, no three, no firebase — a twin of this file is generated
//    for the web client (tools/gen-twins.mjs); the tests load it directly.

import { isValidSpec, normalizeSpec, specKey } from './avatarKit';

/* ─── the document ─────────────────────────────────────────────────────── */

/** 'allo' — our own kit. The only provider; kept as a field so a document can
 *  say what built it if that ever changes. */

export const AVATAR_DOC_VERSION = 2;

/** A profile has a usable avatar when the document carries a valid spec. */
export function hasAvatar(profile) {
  const a = profile?.avatar;
  return !!(a && a.provider === 'allo' && isValidSpec(a.spec));
}
export function genderOf(spec) {
  return spec?.gender === 'm' ? 'male' : spec?.gender === 'f' ? 'female' : '';
}
export function kitGender(g) {
  return g === 'male' ? 'm' : 'f';
}

/** The document for a spec, before it is saved (the builder's preview). */
export function previewDoc(spec, now) {
  const s = normalizeSpec(spec);
  return {
    provider: 'allo',
    id: specKey(s),
    spec: s,
    gender: genderOf(s),
    bodyType: 'fullbody',
    snapshotUrl: '',
    updatedAt: now,
    version: AVATAR_DOC_VERSION
  };
}

/* ─── the profile document changes ─────────────────────────────────────── */

/**
 * The fields to write on users/{uid} when an avatar is saved.
 *
 * 🟢 THE PICTURE THE AVATAR REPLACES IS KEPT ONCE — the first time. Editing an
 *    avatar that already exists must not "back up" the previous avatar's own
 *    snapshot over the real photograph; a backup only ever holds a photo.
 */
export function avatarSaveFields(profile, spec, snapshotUrl, now) {
  const had = hasAvatar(profile);
  const doc = {
    ...previewDoc(spec, now),
    snapshotUrl
  };
  const out = {
    avatar: doc,
    profileImage: snapshotUrl,
    updatedAt: now
  };
  if (!had) {
    out.avatarBackup = {
      profileImage: profile?.profileImage || null,
      photoURL: profile?.photoURL || null
    };
  }
  return out;
}

/** The fields to write when the avatar is removed: the photo it replaced comes
 *  back (or nothing, if there was none), and both avatar fields go. Callers
 *  turn the `null`s into deleteField(). */
export function avatarRemoveFields(profile, now) {
  const b = profile?.avatarBackup || {};
  return {
    avatar: null,
    avatarBackup: null,
    profileImage: b.profileImage || '',
    updatedAt: now
  };
}

/* ─── who may move ─────────────────────────────────────────────────────── */

/** Whether this avatar may move on its own (peek out, wave, look around) right
 *  now. Dragging it is not motion in this sense — anyone may roll any avatar. */
export function avatarMayMove(a) {
  if (!a.hasAvatar) return false;
  if (a.blocked) return false;
  if (!a.canSeeOnline) return false;
  if (!a.online) return false;
  if (a.visible === false) return false;
  return true;
}

/** The peek is edge-triggered — offline → online — and rate-limited, so a
 *  presence map that flaps does not turn a row into a metronome. */
export const ONLINE_PEEK_COOLDOWN_MS = 45 * 1000;
export function shouldPeekOnPresence(prevOnline, nowOnline, lastPeekAt, now) {
  if (prevOnline || !nowOnline) return false;
  return now - lastPeekAt >= ONLINE_PEEK_COOLDOWN_MS;
}

/** Idle motions for a visible online avatar: seldom, and never in lockstep
 *  across rows — the delay is randomised inside a window. */
export const IDLE_MOTION_MIN_MS = 18 * 1000;
export const IDLE_MOTION_MAX_MS = 42 * 1000;
export function nextIdleDelay(rand) {
  const r = rand < 0 ? 0 : rand > 1 ? 1 : rand;
  return Math.round(IDLE_MOTION_MIN_MS + r * (IDLE_MOTION_MAX_MS - IDLE_MOTION_MIN_MS));
}

/* ─── the live budget ──────────────────────────────────────────────────── */

/** How many live GL views may exist at once, per client. Three is two rows
 *  peeking and a header, or a profile and a tapped viewer with room to spare. */
export const LIVE_BUDGET = 3;
/** 3 = the profile / a full-screen viewer, 2 = the chat header, 1 = a row being
 *  dragged or animating, 0 = a row that would merely like to be live. */
export const PRIORITY = {
  ROW: 0,
  ROW_ACTIVE: 1,
  HEADER: 2,
  FOCUS: 3
};
export function createLiveBudget(max = LIVE_BUDGET) {
  let holders = [];
  const listeners = new Set();
  const evict = h => {
    holders = holders.filter(x => x.key !== h.key);
    listeners.forEach(fn => {
      try {
        fn(h.key);
      } catch {}
    });
  };
  return {
    acquire(key, priority, now = Date.now()) {
      const mine = holders.find(h => h.key === key);
      if (mine) {
        mine.priority = priority;
        return true;
      }
      if (holders.length < max) {
        holders.push({
          key,
          priority,
          since: now
        });
        return true;
      }
      // evict the lowest priority, oldest first — only if strictly lower than the asker
      let victim = null;
      for (const h of holders) {
        if (h.priority >= priority) continue;
        const lower = !victim || h.priority < victim.priority;
        const older = !!victim && h.priority === victim.priority && h.since < victim.since;
        if (lower || older) victim = h;
      }
      if (!victim) return false;
      evict(victim);
      holders.push({
        key,
        priority,
        since: now
      });
      return true;
    },
    reprioritize(key, priority) {
      const h = holders.find(x => x.key === key);
      if (h) h.priority = priority;
    },
    release(key) {
      holders = holders.filter(h => h.key !== key);
    },
    holds(key) {
      return holders.some(h => h.key === key);
    },
    size() {
      return holders.length;
    },
    onEvict(fn) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    _reset() {
      holders = [];
    }
  };
}

/** The one budget each client shares across every live view. */
export const liveBudget = createLiveBudget(LIVE_BUDGET);

/* ─── geometry the scene and the tests agree on ────────────────────────── */

/** How far the avatar rises out of the circle at full peek, as a fraction of the
 *  circle's diameter, and how much taller than the circle a peekable view is. */
export const PEEK_RISE = 0.34;
export const PEEK_EXTRA = 0.45;

/**
 * The hole in the mask, as a polygon in circle units (radius `r`, centre 0,0,
 * y up): the circle, plus — while peeking — a slot of height `h` above it,
 * exactly as wide as the circle, so the head rises past the rim while the
 * shoulders stay clipped by the circle's sides.
 *
 * 🟢 A SIMPLE POLYGON AT EVERY h: at h = 0 it is the circle; for 0 < h < r the
 *    slot's corners (±r, h) poke past the arc and the boundary follows them and
 *    then the arc above; for h ≥ r the arc is inside the slot and the boundary
 *    is straight up the sides. Never self-intersecting, so a triangulator can
 *    take it as a hole.
 */
export function peekHolePolygon(r, h, segments = 48) {
  const pts = [];
  const H = h < 0 ? 0 : h;
  // lower semicircle, clockwise from (r,0) through (0,-r) to (-r,0)
  const n = Math.max(8, Math.floor(segments / 2));
  for (let i = 0; i <= n; i++) {
    const a = -(i / n) * Math.PI; // 0 → -π
    pts.push([r * Math.cos(a), r * Math.sin(a)]);
  }
  if (H >= r) {
    pts.push([-r, H], [r, H]);
    return pts;
  }
  // 0 ≤ H < r: up the left side to H, along to where the arc is at height H,
  // over the arc, along to the right corner, down to (r, 0)
  if (H > 0) pts.push([-r, H]);
  const a0 = Math.PI - Math.asin(H / r); // left arc point at height H
  const a1 = Math.asin(H / r); // right arc point at height H
  const m = Math.max(8, Math.floor(segments / 2));
  for (let i = 0; i <= m; i++) {
    const a = a0 + (a1 - a0) * (i / m); // sweeps over the top, left → right
    pts.push([r * Math.cos(a), r * Math.sin(a)]);
  }
  if (H > 0) pts.push([r, H]);
  return pts;
}
/** "Face until the chest": the portrait shows head-top to chest with a little
 *  air above the hair, framed so that span fills the circle's diameter. The
 *  view may be taller than the circle (the peek slot); the circle sits at the
 *  bottom of it. */
export function portraitFrame(l, diameterPx, viewWpx, viewHpx) {
  const air = (l.top - l.eyes) * 0.35; // room for hair above HeadTop_End
  const span = Math.max(1e-3, l.top + air - l.chest) * 1.04;
  const upp = span / diameterPx;
  const centerY = (l.top + air + l.chest) / 2;
  const radius = diameterPx / 2 * upp;
  const halfW = viewWpx / 2 * upp;
  const bottom = centerY - radius;
  return {
    upp,
    centerY,
    left: -halfW,
    right: halfW,
    bottom,
    top: bottom + viewHpx * upp,
    radius
  };
}
/** The whole figure, feet to hair, with breathing room, for a perspective camera. */
export function fullBodyFrame(l, fovDeg, aspect) {
  const height = Math.max(1e-3, l.top - l.floor) * 1.12;
  const width = Math.max(1e-3, l.halfWidth * 2) * 1.3;
  const vFov = fovDeg * Math.PI / 180;
  const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
  const dV = height / 2 / Math.tan(vFov / 2);
  const dH = width / 2 / Math.tan(hFov / 2);
  const distance = Math.max(dV, dH);
  const targetY = (l.top + l.floor) / 2;
  const visibleH = 2 * distance * Math.tan(vFov / 2);
  const headFraction = (targetY + visibleH / 2 - l.eyes) / visibleH;
  return {
    targetY,
    distance,
    headFraction
  };
}

/** The peek slot height in world units at peek progress `p`. */
export function peekSlotHeight(p, frame, diameterPx) {
  const q = p < 0 ? 0 : p > 1 ? 1 : p;
  return q * PEEK_EXTRA * diameterPx * frame.upp;
}

/** How far the figure rises at peek progress `p`, world units. */
export function peekRise(p, frame, diameterPx) {
  const q = p < 0 ? 0 : p > 1 ? 1 : p;
  return q * PEEK_RISE * diameterPx * frame.upp;
}

/** The pixel size of a peekable live view for a circle of `d`: the circle plus the slot. */
export function peekViewSize(d) {
  return {
    width: d,
    height: Math.round(d * (1 + PEEK_EXTRA))
  };
}
